import { useMutation } from '@apollo/client';
import { LoadingButton } from '@mui/lab';
import { Grid, Typography } from '@mui/material';
import TextField from 'components/Inputs/TextField';
import Section from 'components/Section';
import { useUser } from 'contexts/userContext';
import {
  SUBMIT_MID_YEAR_REVIEW,
  SubmitMidYearReviewResponse,
  SubmitMidYearReviewVariables,
  UPDATE_MID_YEAR_REVIEW,
  UpdateMidYearReviewResponse,
  UpdateMidYearReviewVariables,
} from 'graphql/mutation/userGoals';
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { User, UserGoal, UserStatusEnum } from 'types';

type RowType = {
  _id: string;
  empCommentMidYear?: string;
  managerCommentMidYear?: string;
  user?: User;
};

const MidYearEvaluationForm: React.FC<{ initialRows: UserGoal[]; employee: User }> = ({
  initialRows,
  employee,
}) => {
  const [rows, setRows] = useState<RowType[]>(initialRows);

  const { user: loggedInUser } = useUser();

  const [updateMidYearUserGoals, { loading: updatingMidYearUserGoals }] = useMutation<
    UpdateMidYearReviewResponse,
    UpdateMidYearReviewVariables
  >(UPDATE_MID_YEAR_REVIEW);

  const [submitMidYearUserGoals, { loading: submittingUserGoals }] = useMutation<
    SubmitMidYearReviewResponse,
    SubmitMidYearReviewVariables
  >(SUBMIT_MID_YEAR_REVIEW);

  const navigate = useNavigate();

  const isManager = loggedInUser?._id === employee.reportingManager?._id;
  const isThemself = loggedInUser?._id === employee._id;
  const isUserSaved =
    employee.annualUserInfo?.midYearStatus === UserStatusEnum.DRAFT ||
    employee.annualUserInfo?.midYearStatus === UserStatusEnum['NOT STARTED'];
  const isAdmin = loggedInUser?.role.name === 'ADMIN';

  const shouldEnableEdit = useMemo(() => {
    if (isManager) {
      switch (employee.annualUserInfo?.midYearStatus) {
        case UserStatusEnum['PENDING FOR APPROVAL']:
          return true;
        default:
          return false;
      }
    } else if (isThemself) {
      switch (employee.annualUserInfo?.midYearStatus) {
        case UserStatusEnum.DRAFT:
        case UserStatusEnum['NOT STARTED']:
          return true;
        default:
          return false;
      }
    } else if (isAdmin) {
      switch (employee.annualUserInfo?.startYearStatus) {
        case UserStatusEnum.DRAFT:
        case UserStatusEnum['NOT STARTED']:
          return false;
        default:
          return true;
      }
    }

    return false;
  }, [isManager, isThemself, rows, isAdmin]);

  const validateSubmitFields = () => {
    for (let idx = 0; idx < rows.length; idx++) {
      const r = rows[idx];
      if (isThemself && !!!(r.empCommentMidYear ?? '').trim()) {
        return false;
      } else if (!isThemself && !!!(r.managerCommentMidYear ?? '').trim()) {
        return false;
      }
    }
    return true;
  };

  const shouldDisableSubmit = useMemo(() => {
    if (validateSubmitFields()) {
      if (isThemself) {
        switch (employee.annualUserInfo?.midYearStatus) {
          case UserStatusEnum.DRAFT:
          case UserStatusEnum['NOT STARTED']:
            return false;
          default:
            return true;
        }
      } else if (
        isManager &&
        employee.annualUserInfo?.midYearStatus === UserStatusEnum['PENDING FOR APPROVAL']
      ) {
        return false;
      } else if (isAdmin) {
        switch (employee.annualUserInfo?.startYearStatus) {
          case UserStatusEnum.DRAFT:
          case UserStatusEnum['NOT STARTED']:
            return true;
          default:
            return false;
        }
      }
    }
    return true;
  }, [employee, loggedInUser, rows, isThemself, isAdmin]);

  const handleSaveUserGoals = () => {
    const refinedRows = rows.map(currRow => {
      return {
        userId: employee._id,
        userGoalId: currRow._id,
        empCommentMidYear: !!currRow.empCommentMidYear ? currRow.empCommentMidYear : null,
        managerCommentMidYear: !!currRow.managerCommentMidYear
          ? currRow.managerCommentMidYear
          : null,
      } as UpdateMidYearReviewVariables['input']['0'];
    });

    updateMidYearUserGoals({
      variables: {
        input: refinedRows,
      },
      onCompleted: res => {
        toast.success('Saved Successfully');
        setRows(res.updateMidYearUserGoals);
      },
    });
  };

  const handleSubmitMidYearReviews = () => {
    const refinedRows = rows.map(currRow => {
      return {
        userId: employee._id,
        userGoalId: currRow._id,
        empCommentMidYear: currRow.empCommentMidYear,
        managerCommentMidYear: currRow.managerCommentMidYear,
      } as SubmitMidYearReviewVariables['input']['0'];
    });

    submitMidYearUserGoals({
      variables: {
        input: refinedRows,
      },
      onCompleted: res => {
        setRows(res.submitMidYearUserGoals.userGoals);
        toast.success('Employee review have been submitted successfully');
      },
    });
  };

  return (
    <form>
      <Grid container direction="column" rowGap={2.5} mb={14} position="relative">
        {rows.map((item, idx) => (
          <ObjectiveLineItem
            idx={idx}
            userGoal={item as UserGoal}
            setRows={setRows}
            rows={rows}
            key={idx}
            editable={shouldEnableEdit}
            employee={employee}
          />
        ))}
        <Grid
          container
          display="flex"
          justifyContent="center"
          alignItems="center"
          position="sticky"
          bottom={0}
          right={0}
          left={0}
          py={3}
          px={3}
          bgcolor="white"
          zIndex={10}
          sx={{
            borderTopLeftRadius: 4,
            borderTopRightRadius: 4,
          }}
          boxShadow={2}
          rowGap={2}
        >
          <Grid container item direction={'column'} xs={12} md={6} lg={8}>
            <Typography color={'crimson'} fontWeight={600}>
              {(isThemself ? '*Employee' : '*Manager') +
                ' Mid Year Reviews is required to be filled in order to Submit.'}
            </Typography>
          </Grid>
          <Grid
            item
            display="flex"
            justifyContent="flex-end"
            alignItems="center"
            columnGap={1.5}
            xs={12}
            md={6}
            lg={4}
          >
            {((isManager &&
              employee.annualUserInfo?.midYearStatus === UserStatusEnum['PENDING FOR APPROVAL']) ||
              (isThemself && isUserSaved) ||
              (isAdmin && !isUserSaved)) && (
              <LoadingButton
                variant="contained"
                onClick={handleSaveUserGoals}
                loading={updatingMidYearUserGoals}
                disabled={employee.annualUserInfo?.midYearStatus === UserStatusEnum.APPROVED}
              >
                Save
              </LoadingButton>
            )}
            {isThemself && (
              <LoadingButton
                variant="contained"
                disabled={shouldDisableSubmit}
                loading={submittingUserGoals}
                onClick={handleSubmitMidYearReviews}
              >
                Submit
              </LoadingButton>
            )}
          </Grid>
        </Grid>
      </Grid>
    </form>
  );
};

const ObjectiveLineItem: React.FC<{
  idx: number;
  userGoal: UserGoal;
  rows: RowType[];
  editable: boolean;
  setRows: React.Dispatch<React.SetStateAction<RowType[]>>;
  employee: User;
}> = ({ userGoal, setRows, idx, editable, employee }) => {
  const { user: loggedInUser } = useUser();
  const isThemself = loggedInUser?._id === employee._id;

  const handleChange = (fieldName: string, value: string) => {
    setRows(prev =>
      prev.map(curr => {
        if (curr._id === userGoal._id) {
          return { ...curr, [fieldName]: (value ?? '').trimStart() };
        } else return curr;
      })
    );
  };

  return (
    <Grid item>
      <Section title={'Serial No. ' + (idx + 1).toString()} collapsible>
        <Grid container rowGap={1.5} columnSpacing={1.5} alignItems="center" px={0.5} py={0.5}>
          <Grid item xs={12}>
            <TextField
              size="small"
              multiline
              minRows={2}
              label="KRA"
              disabled
              value={userGoal.kra ?? ''}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              size="small"
              multiline
              disabled
              minRows={2}
              label="KPI"
              value={userGoal.kpi ?? ''}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField size="small" label="Target" disabled value={userGoal.target ?? ''} />
          </Grid>
          <Grid item xs={6}>
            <TextField size="small" label="Weightage" disabled value={userGoal.weightage ?? ''} />
          </Grid>
          <Grid item xs={12}>
            <TextField
              multiline
              disabled={!(editable && isThemself)}
              value={userGoal.empCommentMidYear ?? ''}
              label="Employee Mid Year Review"
              onChange={e => handleChange('empCommentMidYear', e.target.value)}
              size="small"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              multiline
              disabled={!(editable && !isThemself)}
              value={userGoal.managerCommentMidYear ?? ''}
              label="Manager Mid Year Review"
              onChange={e => handleChange('managerCommentMidYear', e.target.value)}
              size="small"
            />
          </Grid>
        </Grid>
      </Section>
    </Grid>
  );
};

export default MidYearEvaluationForm;
